import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/api';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const PrincipalApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [academicYears, setAcademicYears] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [remarks, setRemarks] = useState('');

  // Fetch academic years first
  useEffect(() => {
    fetchAcademicYears();
  }, []);

  // Fetch applications when academic year changes
  useEffect(() => {
    if (selectedAcademicYear) {
      fetchApplications();
    }
  }, [selectedAcademicYear]);

  const fetchAcademicYears = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admissions/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const years = response.data.map((form) => form.academicYear).sort().reverse();
      setAcademicYears(years);
      if (years.length > 0) {
        setSelectedAcademicYear(years[0]);
      } else {
        // No admission forms/years exist yet — fetchApplications() will
        // never run in that case, so this is the only place left to clear
        // the initial loading state. Without it the page spins forever.
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching academic years:', err);
      setError('Failed to load academic years');
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE}/admissions/applications/${selectedAcademicYear}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setApplications(response.data.applications || []);
      setError('');
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !selectedApplication) return;

    try {
      setUpdating(true);
      await axios.put(
        `${API_BASE}/admissions/applications/${selectedApplication.applicationNumber}/status`,
        {
          status: newStatus,
          remarks: remarks,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Update local state
      setApplications((prev) =>
        prev.map((app) =>
          app.applicationNumber === selectedApplication.applicationNumber
            ? {
                ...app,
                status: newStatus,
                remarks: remarks,
              }
            : app
        )
      );

      setShowModal(false);
      setSelectedApplication(null);
      setNewStatus('');
      setRemarks('');
    } catch (err) {
      console.error('Error updating application:', err);
      setError('Failed to update application status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-800',
      'under-review': 'bg-yellow-100 text-yellow-800',
      shortlisted: 'bg-purple-100 text-purple-800',
      rejected: 'bg-red-100 text-red-800',
      accepted: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      submitted: '📝',
      'under-review': '👀',
      shortlisted: '⭐',
      rejected: '❌',
      accepted: '✅',
    };
    return icons[status] || '❓';
  };

  const filteredApplications =
    statusFilter === 'all'
      ? applications
      : applications.filter((app) => app.status === statusFilter);

  if (loading && applications.length === 0) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading applications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">
                📮 Admission Applications
              </h1>
              <p className="text-sm text-gray-500">
                View and manage all admission applications received
              </p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Academic Year Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Academic Year
              </label>
              <select
                value={selectedAcademicYear}
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Applications</option>
                <option value="submitted">Submitted</option>
                <option value="under-review">Under Review</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistics */}
        {applications.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{applications.length}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
            <div className="bg-blue-50 rounded-lg shadow p-4 text-center border-l-4 border-blue-500">
              <p className="text-2xl font-bold text-blue-600">
                {applications.filter((a) => a.status === 'submitted').length}
              </p>
              <p className="text-sm text-blue-700">Submitted</p>
            </div>
            <div className="bg-yellow-50 rounded-lg shadow p-4 text-center border-l-4 border-yellow-500">
              <p className="text-2xl font-bold text-yellow-600">
                {applications.filter((a) => a.status === 'under-review').length}
              </p>
              <p className="text-sm text-yellow-700">Under Review</p>
            </div>
            <div className="bg-green-50 rounded-lg shadow p-4 text-center border-l-4 border-green-500">
              <p className="text-2xl font-bold text-green-600">
                {applications.filter((a) => a.status === 'accepted').length}
              </p>
              <p className="text-sm text-green-700">Accepted</p>
            </div>
            <div className="bg-red-50 rounded-lg shadow p-4 text-center border-l-4 border-red-500">
              <p className="text-2xl font-bold text-red-600">
                {applications.filter((a) => a.status === 'rejected').length}
              </p>
              <p className="text-sm text-red-700">Rejected</p>
            </div>
          </div>
        )}

        {/* Applications Table */}
        {filteredApplications.length > 0 ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-500 to-pink-600 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Application #
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Applicant Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Class/Grade
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">
                      Submitted Date
                    </th>
                    <th className="px-6 py-3 text-center text-sm font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredApplications.map((app) => (
                    <tr
                      key={app._id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-6 py-4 text-sm font-mono font-semibold text-purple-600">
                        {app.applicationNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        {app.fullName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {app.admissionClass || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {app.email || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            app.status
                          )}`}
                        >
                          {getStatusIcon(app.status)} {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(app.submittedAt).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                            setNewStatus(app.status);
                            setRemarks(app.remarks || '');
                            setShowModal(true);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1 text-sm font-semibold text-purple-600 hover:bg-purple-50 rounded-lg transition"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">
              {selectedAcademicYear
                ? 'No applications found for the selected filters'
                : 'Select an academic year to view applications'}
            </p>
          </div>
        )}

        {/* Modal for updating status */}
        {showModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Update Application Status
              </h2>

              {/* Applicant Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Application Number</p>
                    <p className="font-semibold text-gray-800">
                      {selectedApplication.applicationNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Applicant Name</p>
                    <p className="font-semibold text-gray-800">
                      {selectedApplication.fullName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold text-gray-800">
                      {selectedApplication.email || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold text-gray-800">
                      {selectedApplication.phone || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Admission Class</p>
                    <p className="font-semibold text-gray-800">
                      {selectedApplication.admissionClass || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Submitted Date</p>
                    <p className="font-semibold text-gray-800">
                      {new Date(selectedApplication.submittedAt).toLocaleDateString(
                        'en-IN'
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Update */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Update Status
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="submitted">Submitted</option>
                    <option value="under-review">Under Review</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Remarks (Optional)
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Add any remarks or feedback for the applicant..."
                  ></textarea>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedApplication(null);
                    setNewStatus('');
                    setRemarks('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold transition"
                >
                  {updating ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PrincipalApplications;
